import { Link, useParams } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useMemo, useState } from "react";
import { MarginTrend } from "@/components/charts/MarginTrend";
import { RevenueCostBars } from "@/components/charts/RevenueCostBars";
import { PageHeaderBar, PageHeaderMetric } from "@/components/layout/PageHeaderBar";
import { usePageHeader } from "@/components/layout/PageHeaderContext";
import { AccountPnl } from "@/components/profitability/AccountPnl";
import { accountRangeTotals } from "@/components/profitability/accountTotals";
import { BudgetCell } from "@/components/profitability/BudgetCell";
import { ClientPartnerCell } from "@/components/profitability/ClientPartnerCell";
import { ContractTypeSelect } from "@/components/profitability/ContractTypeSelect";
import { DiagnosticsPanel } from "@/components/profitability/DiagnosticsPanel";
import { DrillInDialog } from "@/components/profitability/DrillInDialog";
import { ForecastBadge } from "@/components/profitability/ForecastBadge";
import { MarginBadge } from "@/components/profitability/MarginBadge";
import { MarginCell } from "@/components/profitability/MarginCell";
import { MomCell } from "@/components/profitability/MomCell";
import { AccountMonthToDateCard } from "@/components/profitability/MonthToDate";
import { monthOverMonth } from "@/components/profitability/monthOverMonth";
import { AccountProjectedMarginPanel } from "@/components/profitability/ProjectedMargin";
import { RangeTotals } from "@/components/profitability/RangeTotals";
import { rollUpProjects } from "@/components/profitability/rollUpProjects";
import { AccountScheduledBillingPanel } from "@/components/profitability/ScheduledBilling";
import { DataTable, ROW_CLASS } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { PanelTitle } from "@/components/shared/panel-title";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAccountDetail } from "@/hooks/use-account-detail";
import { useAccountPnl } from "@/hooks/use-account-pnl";
import { useClientSort } from "@/hooks/use-client-sort";
import { useContractTypeParam } from "@/hooks/use-contract-type-param";
import { useHidesPersonLevel, useMayRequestPnl } from "@/hooks/use-my-access";
import { useRangeParam } from "@/hooks/use-range-param";
import { useTabParam } from "@/hooks/use-tab-param";
import { COLUMN_HELP } from "@/lib/column-help";
import { money, optionalHours, optionalMoney } from "@/lib/format";
import type { AccountDetail, ClientPartner } from "@/lib/profitability-api";

/**
 * Publishes the account's identity, its profit over the selected range, and the
 * range selector into the app header.
 *
 * A separate component because the page returns early while loading, and a hook
 * cannot live after a conditional return. It renders nothing itself.
 */
function AccountHeader({
  name,
  owner,
  profit,
  marginPct,
  benchmark,
  isClosed,
  rangeInfo,
  range,
  onRangeChange,
  contractTypes,
  contractType,
  onContractTypeChange,
  resaleNote,
}: {
  name: string;
  /** The inferred Client Partner, or null when none can be worked out. */
  owner: ClientPartner | null;
  profit: number;
  marginPct: number | null;
  benchmark: number | null;
  isClosed: boolean | null;
  rangeInfo: AccountDetail["range"];
  range: string;
  onRangeChange: (key: string) => void;
  contractTypes: string[] | undefined;
  contractType: string;
  onContractTypeChange: (key: string) => void;
  /** Blended margin alone misreads an account that is half resale. */
  resaleNote: string | null;
}) {
  const rangeLabel = rangeInfo?.label ?? "";
  usePageHeader(
    () => (
      <PageHeaderBar
        eyebrow={
          <Link
            to="/dashboard/accounts"
            className="w-fit text-xs text-muted-foreground hover:text-foreground hover:underline"
          >
            Accounts
          </Link>
        }
        title={name}
        titleBadge={isClosed !== null ? <ForecastBadge isClosed={isClosed} /> : null}
        fields={[
          {
            // Who carries this account. It sits BEFORE the money so the bar
            // reads name / owner / result.
            key: "owner",
            label: "Client Partner",
            content: <ClientPartnerCell owner={owner} className="text-sm font-medium" />,
            widthClass: "max-w-[12rem]",
          },
          {
            key: "profit",
            label: `Profit${rangeInfo ? ` · ${rangeLabel}` : ""}`,
            content: (
              <PageHeaderMetric
                value={money(profit)}
                badge={<MarginBadge value={marginPct} benchmark={benchmark} />}
                note={resaleNote}
                noteTitle="The services benchmark applies to the services half only."
              />
            ),
          },
        ]}
        filters={
          <ContractTypeSelect
            types={contractTypes}
            value={contractType}
            onChange={onContractTypeChange}
          />
        }
        range={rangeInfo}
        rangeValue={range}
        onRangeChange={onRangeChange}
      />
    ),
    [
      name,
      owner,
      profit,
      marginPct,
      benchmark,
      isClosed,
      rangeInfo,
      rangeLabel,
      range,
      onRangeChange,
      contractTypes,
      contractType,
      onContractTypeChange,
      resaleNote,
    ],
  );
  return null;
}

// A stable empty array for the sort hooks while the page is still loading.
// A fresh [] each render would give useMemo a new dependency every time.
const EMPTY_ROWS: never[] = [];

export function AccountDetailPage() {
  const { accountId } = useParams({ from: "/dashboard/accounts/$accountId" });
  // Same param the accounts list writes, so arriving from a row shows the
  // months that row was summarising rather than the account's whole history.
  const [range, setRange] = useRangeParam();
  const [contractType, setContractType] = useContractTypeParam();
  const { data, isLoading, isError, error, refetch } = useAccountDetail(
    accountId,
    range,
    contractType,
  );
  // Straight off the payload. It briefly took a second request to the list
  // endpoint, because inferring the owner from the rows the detail DOES carry
  // disagreed with the accounts list on 7 of 30 accounts -- the subject now
  // carries client_partner itself, so there is nothing to reconcile.
  const owner = data?.account.client_partner ?? null;
  const [focusPeriod, setFocusPeriod] = useState<string | null>(null);
  const [focusProject, setFocusProject] = useState<string | null>(null);
  const [tabParam, setTab] = useTabParam("overview");
  // Aggregate users (Client Partners, CSMs) get no P&L -- the engine refuses
  // it, since its Employee Cost line is a payroll breakdown -- and no project
  // cost or hours. A pasted ?tab=pnl link lands them on Overview instead.
  const hidesPersonLevel = useHidesPersonLevel(data?.access);
  const mayRequestPnl = useMayRequestPnl(data?.access);
  const tab = hidesPersonLevel && tabParam === "pnl" ? "overview" : tabParam;
  // Fetched only once the tab is opened. The statement is a second round trip
  // to the engine and most visits to this page never leave Overview.
  const pnl = useAccountPnl(accountId, range, tab === "pnl" && mayRequestPnl);

  // No back link: Accounts is one click away in the sidebar, which is always on
  // screen. It was removed from the loaded view, and these two branches have to
  // match or it flashes in during every load.
  const periodSort = useClientSort(
    data?.periods ?? EMPTY_ROWS,
    (row, key) =>
      key === "period"
        ? (row.period_start ?? row.period ?? "")
        : (row[key as keyof typeof row] as number | null),
    { key: "period", dir: "desc" },
  );
  // One row per project, not one per project per month: a 12-month SOW filled
  // twelve near-identical rows and no row was the project.
  const rolledProjects = useMemo(
    () => rollUpProjects(data?.projects ?? EMPTY_ROWS),
    [data?.projects],
  );
  const projectSort = useClientSort(
    rolledProjects,
    (row, key) => row[key as keyof typeof row] as string | number | null,
    { key: "revenue", dir: "desc" },
  );

  if (isError) {
    return (
      <div className="w-full space-y-4">
        <ErrorState message={(error as Error)?.message} onRetry={() => refetch()} />
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="w-full space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  // The most recent period in the series, whatever it is -- closed or an
  // in-flight forecast. That is the number the heading badges against.
  const latest = data.periods.at(-1) ?? null;
  // Charts read oldest-to-newest: time runs left to right. The table below runs
  // newest-first, which is the right default for looking up a recent figure.
  // Sort on period_start, the real date. Sorting on `period` -- the display
  // name -- is alphabetical: "April 2024" lands before "December 2023", and the
  // axis reads April, December, January, June. That bug has now appeared twice
  // in this codebase, which is why the date travels with the row.
  const chronological = [...data.periods].sort((a, b) =>
    (a.period_start ?? a.period ?? "").localeCompare(b.period_start ?? b.period ?? ""),
  );
  // Newest first for the trend table -- the accounts list and projects grid
  // both read newest-to-oldest, so the account's own history should too.
  const chartPoints = chronological.map((row) => ({
    label: row.period ?? row.period_id,
    value: row.margin_pct,
    id: row.period_id,
    provisional: row.period_is_closed === false,
  }));
  const chartMoney = chronological.map((row) => ({
    label: row.period ?? row.period_id,
    revenue: row.revenue,
    cost: row.cost,
    id: row.period_id,
    provisional: row.period_is_closed === false,
  }));
  // Profit over the SELECTED range, not the latest month: the header sits
  // beside the range selector, so it has to answer "what did this account make
  // over these months". One derivation, shared with the totals tiles on
  // Overview -- see accountTotals.ts for why that matters.
  const totals = accountRangeTotals(data.periods);

  // Keyed on period id and derived from the chronological series, so a reader
  // who sorts the trend table by revenue still sees each month compared with
  // the month before it rather than with whatever row floated above it.
  const momByPeriod = monthOverMonth(chronological);

  // Every column sorts. The accessor maps a column key to the value to order
  // on -- the Period column DISPLAYS the period name but sorts on period_start,
  // because ordering on the name is alphabetical.

  // The resale split. Shown only when it is material AND there is enough
  // services revenue for the services margin to mean anything: one account
  // reads -1,227.89% services margin on $75 of residual revenue, which is
  // noise dressed as a finding.
  const passthroughPct = data.totals?.passthrough_revenue_pct ?? null;
  const servicesMargin = data.totals?.services_margin_pct ?? null;
  const servicesRevenue = data.totals?.services_revenue ?? 0;
  const showResaleSplit =
    passthroughPct !== null &&
    passthroughPct >= 10 &&
    servicesMargin !== null &&
    servicesRevenue >= 10_000;

  // A month focused under the previous range may not exist in this one; fall
  // back to no focus rather than leaving an empty drill-in table open.
  const focusProjectRows = focusProject
    ? [...data.projects]
        .filter((row) => row.entity_id.toUpperCase() === focusProject.toUpperCase())
        .sort((a, b) =>
          (b.period_start ?? b.period ?? "").localeCompare(a.period_start ?? a.period ?? ""),
        )
    : [];
  const focusProjectName =
    focusProjectRows[0]?.project_name ?? focusProjectRows[0]?.entity_id ?? "";

  // The account's own figures for the focused month, as the server reported
  // them -- nothing here re-derives the month's margin.
  const focusPeriodRow = data.periods.find((row) => row.period_id === focusPeriod) ?? null;
  const focusLabel = focusPeriodRow?.period ?? null;
  // Biggest first. The question a month drill-in answers is "what moved this
  // month", and the answer belongs at the top rather than wherever the API
  // happened to put it -- the same reason the projects table opens on revenue.
  const focusProjects = focusPeriod
    ? [...data.projects]
        .filter((row) => row.period_id === focusPeriod)
        .sort((a, b) => b.revenue - a.revenue)
    : [];
  // The listed projects are NOT the whole month: account-management demands
  // roll up to the account and are excluded from project profitability, so the
  // rows can sum short of the account's own figure. Reported rather than left
  // for the reader to discover by adding the column up and being puzzled.
  const focusProjectsRevenue = focusProjects.reduce((sum, row) => sum + row.revenue, 0);
  const focusUnattributed =
    focusPeriodRow === null ? 0 : focusPeriodRow.revenue - focusProjectsRevenue;

  return (
    <div className="w-full space-y-4">
      <AccountHeader
        name={data.account.account_name ?? data.account.account_id}
        owner={owner}
        profit={totals.profit}
        marginPct={totals.marginPct}
        benchmark={data.benchmark_pct}
        isClosed={latest?.period_is_closed ?? null}
        rangeInfo={data.range}
        range={range}
        onRangeChange={setRange}
        resaleNote={
          showResaleSplit
            ? `${servicesMargin.toFixed(1)}% services · ${passthroughPct.toFixed(0)}% of revenue is resale`
            : null
        }
        contractTypes={data.contract_types}
        contractType={contractType}
        onContractTypeChange={setContractType}
      />

      {/* Tabs, not one tall scroll. Diagnostics, two charts, a period table
          and a project table stacked in one column meant "which project is
          dragging this account" sat below two screens of chart. One question
          per tab. */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="projects">Projects</TabsTrigger>
          <TabsTrigger value="trend">Trend</TabsTrigger>
          {/* Its own tab, not a card on Overview: the statement is twelve rows
              across five columns and it answers a different question from the
              rest of the page -- "what does finance have booked against this
              account", not "how is delivery doing". */}
          {hidesPersonLevel ? null : <TabsTrigger value="pnl">P&amp;L</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* Before the diagnostics: the five figures everything else on the
              page qualifies. They were previously readable only by summing the
              trend table by hand. */}
          <RangeTotals
            totals={totals}
            rangeLabel={data.range?.label ?? ""}
            benchmarkPct={data.benchmark_pct}
            trend={chartPoints.map((p) => p.value)}
          />
          {/* The narrative beside the chart it explains: why the margin is what
              it is on the left, the revenue and cost bars it comes from on the
              right. Clicking a month drills to that month's projects. */}
          <div
            data-overview-grid
            className="grid w-full gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]"
          >
            <DiagnosticsPanel diagnostics={data.diagnostics} />
            <Card className="w-full py-4">
              <CardContent>
                <RevenueCostBars
                  months={chartMoney}
                  subtitle="The gap between the bars is the margin"
                  onSelect={(month) => setFocusPeriod(month.id ?? null)}
                />
              </CardContent>
            </Card>
          </div>
          {/* The account's T&M projects in the open month, live; absent when it
              has no T&M work. Summed by the engine, never here, and never added
              to the totals above. */}
          {data.month_to_date ? <AccountMonthToDateCard slice={data.month_to_date} /> : null}
          {/* Milestones about to be invoiced, by project. A forecast beside the
              closed-period totals above, never added into them. */}
          <AccountScheduledBillingPanel schedule={data.scheduled_billing} />
          {/* Open fixed-price projects only; absent when the account has
              none. Summed dollars from the engine, never averaged here. */}
          {data.projected_margin ? (
            <AccountProjectedMarginPanel projection={data.projected_margin} />
          ) : null}
          <Card className="w-full py-4">
            <CardContent>
              <MarginTrend
                points={chartPoints}
                benchmark={data.benchmark_pct}
                subtitle="Click a month to see that month's projects"
                onSelect={(point) => setFocusPeriod(point.id ?? null)}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="projects" className="space-y-4">
          <Card flush className="w-full">
            <CardHeader>
              <PanelTitle>Projects</PanelTitle>
            </CardHeader>
            <CardContent className="p-0">
              <DataTable
                flush
                headers={[
                  { label: "Project", sortKey: "projectName", help: COLUMN_HELP.project },
                  {
                    label: "Months",
                    className: "text-right",
                    sortKey: "periods",
                    help: COLUMN_HELP.months,
                  },
                  {
                    label: "Revenue",
                    className: "text-right",
                    sortKey: "revenue",
                    help: COLUMN_HELP.revenue,
                  },
                  ...(hidesPersonLevel
                    ? []
                    : [
                        {
                          label: "Cost",
                          className: "text-right",
                          sortKey: "cost",
                          help: COLUMN_HELP.cost,
                        },
                      ]),
                  {
                    label: "Margin",
                    className: "text-right",
                    sortKey: "marginPct",
                    help: COLUMN_HELP.margin,
                  },
                  {
                    label: "Hours vs budget",
                    className: "text-right",
                    sortKey: "variancePct",
                    help: COLUMN_HELP.hoursVsBudget,
                  },
                ]}
                sort={projectSort.sort}
                sortDir={projectSort.sortDir}
                onSort={projectSort.toggleSort}
                empty={
                  <EmptyState
                    title="No projects in this range"
                    description="This account has no project revenue or cost in the months selected."
                  />
                }
              >
                {projectSort.rows.map((row) => (
                  <tr
                    key={row.entityId}
                    className={`cursor-pointer ${ROW_CLASS}`}
                    onClick={() => setFocusProject(row.entityId)}
                  >
                    <td className="px-5 py-3">
                      <Link
                        to="/dashboard/projects/$demandId"
                        params={{ demandId: row.entityId }}
                        className="font-medium hover:underline"
                        // The row opens the month breakdown; the name is still the
                        // way to the project's own page, so it must not do both.
                        onClick={(e) => e.stopPropagation()}
                      >
                        {row.projectName}
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums text-muted-foreground">
                      {row.periods}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">{money(row.revenue)}</td>
                    {hidesPersonLevel ? null : (
                      <td className="px-5 py-3 text-right tabular-nums">
                        {row.cost === null ? "—" : money(row.cost)}
                      </td>
                    )}
                    <td className="px-5 py-3 text-right">
                      <MarginCell value={row.marginPct} />
                    </td>
                    <td className="px-5 py-3 text-right">
                      <BudgetCell variancePct={row.variancePct} hasBudget={row.hasBudget} />
                    </td>
                  </tr>
                ))}
              </DataTable>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trend" className="space-y-4">
          <Card flush className="w-full">
            <CardHeader>
              <PanelTitle>Margin by period</PanelTitle>
              {/* The same drill-in the two charts on Overview open. A row here
                  and a point there are the same month, so they must not lead to
                  different places -- or to nowhere, which is where this table
                  led until now. */}
              <p className="text-sm text-muted-foreground">
                Click a month to see that month's projects.
              </p>
            </CardHeader>
            <CardContent className="p-0">
              <DataTable
                flush
                headers={[
                  { label: "Period", sortKey: "period", help: COLUMN_HELP.period },
                  {
                    label: "Revenue",
                    className: "text-right",
                    sortKey: "revenue",
                    help: COLUMN_HELP.revenue,
                  },
                  {
                    label: "Cost",
                    className: "text-right",
                    sortKey: "cost",
                    help: COLUMN_HELP.cost,
                  },
                  {
                    label: "Margin",
                    className: "text-right",
                    sortKey: "margin_pct",
                    help: COLUMN_HELP.margin,
                  },
                  // Deliberately not sortable. The figure is a comparison with
                  // the row's own prior month, not a property of the row, and
                  // ranking months by "who grew most" is a different table.
                  { label: "MoM", className: "text-right", help: COLUMN_HELP.mom },
                ]}
                sort={periodSort.sort}
                sortDir={periodSort.sortDir}
                onSort={periodSort.toggleSort}
                empty={
                  <EmptyState
                    title="No periods on record"
                    description="This account has no margin history."
                  />
                }
              >
                {periodSort.rows.map((row) => (
                  <tr
                    key={row.period_id}
                    className={`cursor-pointer ${ROW_CLASS}`}
                    onClick={() => setFocusPeriod(row.period_id)}
                  >
                    <td className="px-5 py-3">
                      <span className="flex items-center gap-2">
                        {/* The row carries the click for the mouse; this button
                            is the same drill-in for a keyboard, and it is what
                            makes the month look clickable at all. */}
                        <button
                          type="button"
                          className="font-medium hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFocusPeriod(row.period_id);
                          }}
                        >
                          {row.period ?? row.period_id}
                        </button>
                        <ForecastBadge isClosed={row.period_is_closed} />
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">{money(row.revenue)}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{money(row.cost)}</td>
                    <td className="px-5 py-3 text-right">
                      <MarginCell value={row.margin_pct} />
                    </td>
                    <td className="px-5 py-3 text-right">
                      <MomCell change={momByPeriod.get(row.period_id)} />
                    </td>
                  </tr>
                ))}
              </DataTable>
            </CardContent>
          </Card>
        </TabsContent>

        {hidesPersonLevel ? null : (
          <TabsContent value="pnl" className="space-y-4">
            <Card flush className="w-full">
              <CardHeader>
                <PanelTitle>Profit &amp; loss</PanelTitle>
                {/* The scope split is the one thing a reader has to be told: the
                  years ignore the range selector at the top of the page and the
                  last column does not. Left to infer it, the obvious reading is
                  that the years sum to the range, and they do not. */}
                <p className="text-sm text-muted-foreground">
                  NOS's own statement, in NOS's order. Year columns are whole calendar years; the
                  last column is the range selected above. Subtotals come from NOS, not from summing
                  the rows.
                </p>
              </CardHeader>
              <CardContent className="p-0">
                {pnl.isError ? (
                  <div className="p-4">
                    <ErrorState
                      message={(pnl.error as Error)?.message}
                      onRetry={() => pnl.refetch()}
                    />
                  </div>
                ) : pnl.isLoading || !pnl.data ? (
                  <div className="space-y-2 p-4">
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                  </div>
                ) : (
                  <AccountPnl pnl={pnl.data} rangeLabel={data.range?.label ?? ""} />
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Modals sit outside the tabs: a month drill-in opened from the
          Overview chart must not unmount when its tab loses focus. */}
      <DrillInDialog
        open={focusProject !== null}
        onOpenChange={(open: boolean) => !open && setFocusProject(null)}
        title={focusProjectName}
        description="Month by month, within the selected range."
        // The drill-in used to end here: it names one project, shows its
        // months, and offered no way to the project's own page -- so the
        // reader closed it and went back to find the row they had just
        // clicked. The month drill-in beside it never had that problem,
        // because its rows ARE projects and each name links out.
        action={
          focusProject ? (
            <Button asChild variant="outline" size="sm">
              <Link to="/dashboard/projects/$demandId" params={{ demandId: focusProject }}>
                Open project page
                <ArrowRight className="size-3.5" aria-hidden />
              </Link>
            </Button>
          ) : null
        }
      >
        <DataTable
          headers={[
            { label: "Period", help: COLUMN_HELP.period },
            { label: "Revenue", className: "text-right", help: COLUMN_HELP.revenue },
            ...(hidesPersonLevel
              ? []
              : [{ label: "Cost", className: "text-right", help: COLUMN_HELP.cost }]),
            { label: "Margin", className: "text-right", help: COLUMN_HELP.margin },
            ...(hidesPersonLevel
              ? []
              : [{ label: "Hours", className: "text-right", help: COLUMN_HELP.hours }]),
          ]}
          empty={<EmptyState title="No months" description="Nothing in the selected range." />}
        >
          {focusProjectRows.map((row) => (
            <tr key={row.period_id} className="border-b last:border-0">
              <td className="px-4 py-3">
                <span className="flex items-center gap-2 whitespace-nowrap">
                  {row.period ?? row.period_id}
                  <ForecastBadge isClosed={row.period_is_closed} />
                </span>
              </td>
              <td className="px-4 py-3 text-right tabular-nums">{money(row.revenue)}</td>
              {hidesPersonLevel ? null : (
                <td className="px-4 py-3 text-right tabular-nums">{optionalMoney(row.cost)}</td>
              )}
              <td className="px-4 py-3 text-right">
                <MarginCell value={row.margin_pct} />
              </td>
              {hidesPersonLevel ? null : (
                <td className="px-4 py-3 text-right tabular-nums">{optionalHours(row.hours)}</td>
              )}
            </tr>
          ))}
        </DataTable>
      </DrillInDialog>

      <DrillInDialog
        open={focusPeriod !== null}
        onOpenChange={(open: boolean) => !open && setFocusPeriod(null)}
        title={`Projects in ${focusLabel ?? "the selected month"}`}
        description="Biggest revenue first. Click a project to open its page."
      >
        <DataTable
          headers={[
            { label: "Project", help: COLUMN_HELP.project },
            { label: "Revenue", className: "text-right", help: COLUMN_HELP.revenue },
            ...(hidesPersonLevel
              ? []
              : [{ label: "Cost", className: "text-right", help: COLUMN_HELP.cost }]),
            { label: "Margin", className: "text-right", help: COLUMN_HELP.margin },
            // Hours last, matching the project drill-in beside it: the two
            // modals open from the same page and must not order the same five
            // columns differently.
            ...(hidesPersonLevel
              ? []
              : [{ label: "Hours", className: "text-right", help: COLUMN_HELP.hours }]),
          ]}
          empty={
            <EmptyState
              title="No projects in this month"
              description="This account logged no project revenue or cost for the month you picked."
            />
          }
        >
          {focusProjects.length > 0 && [
            ...focusProjects.map((row) => (
              <tr key={`${row.entity_id}-${row.period_id}`} className="border-b last:border-0">
                <td className="px-4 py-3">
                  <Link
                    to="/dashboard/projects/$demandId"
                    params={{ demandId: row.entity_id }}
                    className="font-medium hover:underline"
                  >
                    {row.project_name ?? row.entity_id}
                  </Link>
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{money(row.revenue)}</td>
                {hidesPersonLevel ? null : (
                  <td className="px-4 py-3 text-right tabular-nums">{optionalMoney(row.cost)}</td>
                )}
                <td className="px-4 py-3 text-right">
                  <MarginCell value={row.margin_pct} />
                </td>
                {hidesPersonLevel ? null : (
                  <td className="px-4 py-3 text-right tabular-nums">{optionalHours(row.hours)}</td>
                )}
              </tr>
            )),
            // The month as the account itself reports it, so the reader can see
            // at once whether the rows above are the whole story. Server
            // figures, not a sum taken here -- a total computed from the rows
            // could only ever agree with the rows.
            ...(focusPeriodRow
              ? [
                  <tr key="account-total" className="border-t-2 bg-muted/30">
                    <th scope="row" className="px-4 py-3 text-left font-semibold">
                      Account total
                    </th>
                    <td className="px-4 py-3 text-right font-semibold tabular-nums">
                      {money(focusPeriodRow.revenue)}
                    </td>
                    {hidesPersonLevel ? null : (
                      <td className="px-4 py-3 text-right font-semibold tabular-nums">
                        {money(focusPeriodRow.cost)}
                      </td>
                    )}
                    <td className="px-4 py-3 text-right font-semibold">
                      <MarginCell value={focusPeriodRow.margin_pct} />
                    </td>
                    {hidesPersonLevel ? null : (
                      <td className="px-4 py-3 text-right font-semibold tabular-nums">
                        {optionalHours(focusPeriodRow.hours)}
                      </td>
                    )}
                  </tr>,
                ]
              : []),
          ]}
        </DataTable>
        {/* Only when the gap would show on screen: money() rounds to the
            dollar, so sub-dollar float drift is not a discrepancy worth a
            sentence. */}
        {focusPeriodRow && Math.abs(focusUnattributed) >= 1 ? (
          <p className="px-1 pt-3 text-xs text-muted-foreground">
            {focusProjects.length === 0
              ? `The account still booked ${money(focusPeriodRow.revenue)} in the month, none of it on a project demand -- account management demands are excluded from project profitability.`
              : focusUnattributed > 0
                ? `The projects above come to ${money(focusProjectsRevenue)} of the account's ${money(focusPeriodRow.revenue)} for the month. The rest is booked to the account rather than to a project demand -- account management demands are excluded from project profitability.`
                : `The projects above come to ${money(focusProjectsRevenue)}, more than the account's own ${money(focusPeriodRow.revenue)} for the month. The two come from different roll-ups, and the account figure is the one every other number on this page is built from.`}
          </p>
        ) : null}
      </DrillInDialog>
    </div>
  );
}
